/**
 * Global Avatar Preloader
 * Ensures avatars are fully loaded in browser memory before rendering
 */

// Cache of preloaded image URLs - stores the URL when image is confirmed loaded
const preloadedAvatars = new Map<string, string>();

// Promise cache to prevent duplicate loading
const loadingPromises = new Map<string, Promise<string>>();

/**
 * Preload an avatar image and cache it
 * Returns a promise that resolves when the image is fully loaded
 */
export async function preloadAvatar(url: string | null): Promise<string | null> {
  if (!url) return null;
  
  // Already preloaded
  if (preloadedAvatars.has(url)) {
    return preloadedAvatars.get(url)!;
  }
  
  // Already loading - return existing promise
  if (loadingPromises.has(url)) {
    return loadingPromises.get(url)!;
  }
  
  // Create loading promise
  const loadPromise = new Promise<string>((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      preloadedAvatars.set(url, url);
      loadingPromises.delete(url);
      resolve(url);
    };
    
    img.onerror = () => {
      loadingPromises.delete(url);
      reject(new Error(`Failed to load avatar: ${url}`));
    };
    
    // Set crossOrigin for CORS images
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
  
  loadingPromises.set(url, loadPromise);
  
  try {
    return await loadPromise;
  } catch {
    return null;
  }
}

/**
 * Check if an avatar URL is already preloaded (sync check)
 */
export function isAvatarPreloaded(url: string | null): boolean {
  if (!url) return false;
  return preloadedAvatars.has(url);
}

/**
 * Get a preloaded avatar URL if available (sync)
 * Returns null if not preloaded
 */
export function getPreloadedAvatar(url: string | null): string | null {
  if (!url) return null;
  return preloadedAvatars.get(url) || null;
}

/**
 * Preload multiple avatars in parallel
 */
export async function preloadAvatars(urls: (string | null)[]): Promise<void> {
  const validUrls = urls.filter((url): url is string => !!url);
  await Promise.allSettled(validUrls.map(url => preloadAvatar(url)));
}

/**
 * Clear the preload cache (useful for memory management)
 */
export function clearAvatarCache(): void {
  preloadedAvatars.clear();
  loadingPromises.clear();
}
